/**
 * Tabbed Collections Component
 *
 * Handles tab switching, tab-bar overflow navigation, and carousel navigation.
 */
class TabbedCollections extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('.tabbed-collections__tab'));
    this.panels = Array.from(this.querySelectorAll('.tabbed-collections__panel'));
    this.prevBtn = this.querySelector('.tabbed-collections__arrow--prev');
    this.nextBtn = this.querySelector('.tabbed-collections__arrow--next');
    this.activeIndex = 0;

    // Tab bar scroll navigation
    this.tabsWrapper = this.querySelector('.tabbed-collections__tabs-wrapper');
    this.tabsScroller = this.querySelector('.tabbed-collections__tabs');
    this.tabsNavPrev = this.querySelector('.tabbed-collections__tabs-nav--prev');
    this.tabsNavNext = this.querySelector('.tabbed-collections__tabs-nav--next');

    // Tab click
    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.switchTab(i));
    });

    // Carousel arrows
    this.prevBtn?.addEventListener('click', () => this.navigate('prev'));
    this.nextBtn?.addEventListener('click', () => this.navigate('next'));

    // Tab bar scroll arrows
    this.tabsNavPrev?.addEventListener('click', () => this.scrollTabs('prev'));
    this.tabsNavNext?.addEventListener('click', () => this.scrollTabs('next'));

    // Watch tab bar scroll position
    if (this.tabsScroller) {
      this.tabsScroller.addEventListener('scroll', () => this.updateTabsOverflow(), { passive: true });

      // Re-check on resize (e.g. window resize, font load)
      if (window.ResizeObserver) {
        new ResizeObserver(() => this.updateTabsOverflow()).observe(this.tabsScroller);
      }

      this.updateTabsOverflow();
    }
  }

  /** Show/hide the tab-bar scroll arrows based on current scroll position */
  updateTabsOverflow() {
    const el = this.tabsScroller;
    const wrapper = this.tabsWrapper;
    if (!el || !wrapper) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    wrapper.classList.toggle('can-scroll-left', scrollLeft > 4);
    wrapper.classList.toggle('can-scroll-right', scrollWidth - clientWidth - scrollLeft > 4);
  }

  /** Scroll the tab bar left or right by roughly 3 tab widths */
  scrollTabs(direction) {
    const el = this.tabsScroller;
    if (!el) return;

    const firstTab = el.querySelector('.tabbed-collections__tab');
    const amount = firstTab ? firstTab.offsetWidth * 3 : 180;

    el.scrollBy({
      left: direction === 'next' ? amount : -amount,
      behavior: 'smooth',
    });
  }

  switchTab(index) {
    if (index === this.activeIndex) return;

    this.tabs.forEach((tab, i) => {
      tab.classList.toggle('is-active', i === index);
      tab.setAttribute('aria-selected', String(i === index));
    });

    this.panels.forEach((panel, i) => {
      panel.classList.toggle('is-active', i === index);
      panel.setAttribute('aria-hidden', String(i !== index));
    });

    this.activeIndex = index;
  }

  /** Returns the slideshow-slides scroller of the active panel */
  getActiveScroller() {
    const panel = this.panels[this.activeIndex];
    return panel ? panel.querySelector('slideshow-slides') : null;
  }

  navigate(direction) {
    const scroller = this.getActiveScroller();
    if (!scroller) return;

    const slide = scroller.querySelector('slideshow-slide');
    if (!slide) return;

    const gap = parseFloat(getComputedStyle(scroller).columnGap) || 0;
    const slideWidth = slide.getBoundingClientRect().width + gap;

    scroller.scrollBy({
      left: direction === 'next' ? slideWidth : -slideWidth,
      behavior: 'smooth',
    });
  }
}

customElements.define('tabbed-collections', TabbedCollections);
